import React, { useEffect, useRef, useState } from 'react'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import { Input } from 'iebt-zing-storybook'
import { postProvider } from 'sharedKernel/api'
import { showToast } from 'components/toast'
import { RadioGroupItemProps } from 'iebt-zing-storybook/dist/components/Fields'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { HotsiteFormMain } from '../..'
import { trans } from 'sharedKernel/i18n'
import { INPUT_MASK_FORMAT, ROUTES } from 'sharedKernel/constants'
import { SectionTitle, StepTransition, FormTitle } from '../../Register/v1/style'
import { LoginButton } from '../../Register/FormActions/Login'
import { applyMaskIfNotEmpty, applyTitleOnError } from 'sharedKernel/handlers'
import {
  blockNonNumeric,
  blockNonNumericMax,
  formatBRL,
  EMAIL_REGEX,
  isValidCPF,
  isValidCNPJ,
  onlyDigits
} from 'sharedKernel/utils'
import { v4 as uuidv4 } from 'uuid'
import { UserTerms } from 'components/UserTerms'
import { CitySelector } from 'components/CitySelector'
import {
  DROPDOWN_BRAZIL_STATES,
  DROPDOWN_ORGAN_TYPE,
  DROPDOWN_POPULATION_SIZE,
  MARCO_LEGAL_OPTIONS,
  OBJETIVOS_OPTIONS,
  VERTICAIS_OPTIONS
} from '../constants'
import {
  AddFocalPointButton,
  AddLogoButton,
  CadastrarButton,
  CheckboxGroup,
  FocalPointCard,
  FocalPointDivider,
  FocalPointTitle,
  FieldLabel,
  FormWrapper,
  LogoPreviewError,
  LogoPreviewImage,
  ProximoButton,
  RemoveFocalPointButton,
  RemoveLogoButton,
  StepButtonsContainer,
  StyledSelect,
  StyledTextarea
} from './style'
import {
  createPublicAdministrationPost,
  fileToProfileImagePayload,
  isSuccessfulPublicAdministrationResponse,
  parseRegisterApiError,
  type ProfileImagePayload
} from 'domain/feature/hotsite/Forms/NewPublicOrganRegister/v1/createPublicAdministrationRequest'

interface IField {
  value: string
  isInvalid: boolean
  message: string
  isRequired: boolean
}

const emptyField = (isRequired = false): IField => ({
  value: '',
  isInvalid: false,
  message: '',
  isRequired
})

interface IFocalPoint {
  id: string
  name: IField
  email: IField
  phone: IField
  cpf: IField
  role: IField
  linkedin: IField
}

const newFocalPoint = (): IFocalPoint => ({
  id: uuidv4(),
  name: emptyField(true),
  email: emptyField(true),
  phone: emptyField(true),
  cpf: emptyField(false),
  role: emptyField(true),
  linkedin: emptyField(false)
})

interface IInstitutionalState {
  organName: IField
  organType: IField
  organTypeOther: IField
  organDescription: IField
  cnpj: IField
  state: IField
  city: IField
  populationSize: IField
  annualBudget: IField
  numberOfServers: IField
  logo: File | null
  logoPreview: string | null
}

const getInitialInstitutionalState = (): IInstitutionalState => ({
  organName: emptyField(true),
  organType: emptyField(true),
  organTypeOther: emptyField(true),
  organDescription: emptyField(true),
  cnpj: emptyField(true),
  state: emptyField(true),
  city: emptyField(true),
  populationSize: emptyField(false),
  annualBudget: emptyField(true),
  numberOfServers: emptyField(true),
  logo: null,
  logoPreview: null
})

interface ILeadershipState {
  leaderName: IField
  mandateUntil: IField
  hasInnovationArea: boolean | null
  hasInnovationAreaInvalid: boolean
  innovationDeptName: IField
  innovationDeptYear: IField
  innovationDeptTeamSize: IField
  hasIctContract: boolean | null
  hasIctContractInvalid: boolean
  ictContractName: IField
  marcoLegal: Record<string, boolean>
  marcoLegalInvalid: boolean
}

const getInitialLeadershipState = (): ILeadershipState => ({
  leaderName: emptyField(true),
  mandateUntil: emptyField(false),
  hasInnovationArea: null,
  hasInnovationAreaInvalid: false,
  innovationDeptName: emptyField(true),
  innovationDeptYear: emptyField(true),
  innovationDeptTeamSize: emptyField(true),
  hasIctContract: null,
  hasIctContractInvalid: false,
  ictContractName: emptyField(true),
  marcoLegal: Object.fromEntries(MARCO_LEGAL_OPTIONS.map(o => [o.id, false])),
  marcoLegalInvalid: false
})

interface IIntentionState {
  verticais: Record<string, boolean>
  verticaisInvalid: boolean
  objetivos: Record<string, boolean>
  objetivosInvalid: boolean
  hasCtibudget: boolean | null
  hasCtibudgetInvalid: boolean
  ctibudgetValue: IField
  acceptTerms: boolean
  acceptTermsInvalid: boolean
}

const getInitialIntentionState = (): IIntentionState => ({
  verticais: Object.fromEntries(VERTICAIS_OPTIONS.map(o => [o.id, false])),
  verticaisInvalid: false,
  objetivos: Object.fromEntries(OBJETIVOS_OPTIONS.map(o => [o.id, false])),
  objetivosInvalid: false,
  hasCtibudget: null,
  hasCtibudgetInvalid: false,
  ctibudgetValue: emptyField(true),
  acceptTerms: false,
  acceptTermsInvalid: false
})

const TOTAL_STEPS = 4

const scrollToFirstInvalidField = (): void => {
  const selectors = [
    '.is-invalid',
    '[aria-invalid="true"]',
    '[data-invalid="true"]'
  ].join(', ')

  const el = document.querySelector<HTMLElement>(selectors)
  if (el == null) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return
  }

  el.scrollIntoView({ behavior: 'smooth', block: 'center' })

  const focusable = el.matches('input, select, textarea, button')
    ? el
    : el.querySelector<HTMLElement>('input, select, textarea, button')

  focusable?.focus({ preventScroll: true })
}

export const PublicAdministrationRegisterForm = (): JSX.Element => {
  const navigate = useNavigate()
  const { mutateAsync: submitPublicAdministration, isLoading: isSubmitting } = postProvider()

  const [focalPoints, setFocalPoints] = useState<IFocalPoint[]>([newFocalPoint()])
  const [institutional, setInstitutional] = useState<IInstitutionalState>(getInitialInstitutionalState())
  const [leadership, setLeadership] = useState<ILeadershipState>(getInitialLeadershipState())
  const [intention, setIntention] = useState<IIntentionState>(getInitialIntentionState())
  const [step, setStep] = useState(0)
  const [logoError, setLogoError] = useState(false)
  const [shouldScrollToError, setShouldScrollToError] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const noAutocompleteIds = ['annualBudget', 'numberOfServers', 'innovationDeptYear', 'innovationDeptTeamSize', 'ctibudgetValue', 'mandateUntil']
    noAutocompleteIds.forEach(id => {
      const el = document.getElementById(id) as HTMLInputElement | null
      if (el) {
        el.autocomplete = 'off'
        el.setAttribute('autocomplete', 'off')
      }
    })
  }, [step])

  useEffect(() => {
    if (!shouldScrollToError) return

    const frameId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scrollToFirstInvalidField()
        setShouldScrollToError(false)
      })
    })

    return () => window.cancelAnimationFrame(frameId)
  }, [shouldScrollToError, step])

  const labels = {
    formTitle: trans('Register and be part of TEIA'),
    titleMessage: trans('Fill in this field'),
    next: trans('Next'),
    back: trans('Back'),
    register: trans('Register'),
    sending: trans('Sending...'),
    registerSuccess: trans('Successfully registered.'),
    registerApiError: trans('Could not complete your registration. Please contact support.'),
    focalPointName: trans('Full name'),
    focalPointEmail: trans('Email'),
    focalPointPhone: trans('Contact phone'),
    focalPointCpf: trans('CPF'),
    focalPointRole: trans('Role'),
    focalPointLinkedin: trans('Linkedin'),
    addFocalPoint: trans('Add focal point'),
    representativesTitle: trans('Representative identification'),
    institutionalTitle: trans('Institutional Identification'),
    leadershipTitle: trans('Leadership and Governance'),
    intentionTitle: trans('Intention with TEIA'),
    invalidCpf: trans('Invalid CPF'),
    invalidPhone: trans('Invalid phone number'),
    invalidFormat: trans('Invalid format'),
    logoRequiredMessage: trans('Upload do logo é obrigatório'),
    incompleteFieldsMessage: trans('Há campos obrigatórios ou inválidos. Revise a etapa destacada.'),
    incompleteStepMessage: trans('Há campos obrigatórios ou inválidos nesta etapa.')
  }

  const updateFocalPoint = (index: number, field: keyof IFocalPoint, value: string): void => {
    setFocalPoints(prev =>
      prev.map((fp, i) =>
        i === index ? { ...fp, [field]: { ...(fp[field] as IField), value } } : fp
      )
    )
  }

  const setFocalPointFieldInvalid = (index: number, field: keyof IFocalPoint, message: string): void => {
    setFocalPoints(prev =>
      prev.map((fp, i) =>
        i === index ? { ...fp, [field]: { ...(fp[field] as IField), isInvalid: true, message } } : fp
      )
    )
  }

  const clearFocalPointFieldError = (index: number, field: keyof IFocalPoint): void => {
    setFocalPoints(prev =>
      prev.map((fp, i) =>
        i === index ? { ...fp, [field]: { ...(fp[field] as IField), isInvalid: false, message: '' } } : fp
      )
    )
  }

  const validateFocalPointField = (index: number, field: keyof IFocalPoint): void => {
    const fp = focalPoints[index]
    const fieldData = fp[field] as IField
    if (fieldData.value.trim() === '') {
      setFocalPointFieldInvalid(index, field, labels.titleMessage)
    } else {
      clearFocalPointFieldError(index, field)
    }
  }

  const addFocalPoint = (): void => {
    setFocalPoints(prev => [...prev, newFocalPoint()])
  }

  const removeFocalPoint = (index: number): void => {
    if (focalPoints.length <= 1) return
    setFocalPoints(prev => prev.filter((_, i) => i !== index))
  }

  const updateInstitutional = (field: keyof IInstitutionalState, value: string): void => {
    setInstitutional(prev => ({
      ...prev,
      [field]: { ...(prev[field] as IField), value, isInvalid: false, message: '' }
    }))
  }

  const updateInstitutionalStateUf = (uf: string): void => {
    setInstitutional(prev => ({
      ...prev,
      state: { ...prev.state, value: uf, isInvalid: false, message: '' },
      ...(prev.state.value !== uf
        ? { city: { ...prev.city, value: '', isInvalid: false, message: '' } }
        : {})
    }))
  }

  const validateInstitutionalField = (field: keyof IInstitutionalState): void => {
    const f = institutional[field] as IField
    if (!f.isRequired) return
    if (f.value.trim() === '') {
      setInstitutional(prev => ({
        ...prev,
        [field]: { ...(prev[field] as IField), isInvalid: true, message: labels.titleMessage }
      }))
    }
  }

  const isValidSection = (section: number): boolean => {
    if (section === 0) {
      let isValid = true
      focalPoints.forEach((fp, index) => {
        if (fp.name.value.trim() === '') { setFocalPointFieldInvalid(index, 'name', labels.titleMessage); isValid = false }

        const emailVal = fp.email.value.trim()
        if (emailVal === '') { setFocalPointFieldInvalid(index, 'email', labels.titleMessage); isValid = false }
        else if (!EMAIL_REGEX.test(emailVal)) { setFocalPointFieldInvalid(index, 'email', labels.invalidFormat); isValid = false }

        const phoneDigits = fp.phone.value.replace(/\D/g, '')
        if (fp.phone.value.trim() === '') { setFocalPointFieldInvalid(index, 'phone', labels.titleMessage); isValid = false }
        else if (phoneDigits.length < 10) { setFocalPointFieldInvalid(index, 'phone', labels.invalidPhone); isValid = false }

        if (fp.role.value.trim() === '') { setFocalPointFieldInvalid(index, 'role', labels.titleMessage); isValid = false }

        const cpfDigits = fp.cpf.value.replace(/\D/g, '')
        if (cpfDigits.length > 0 && (cpfDigits.length !== 11 || !isValidCPF(cpfDigits))) {
          setFocalPointFieldInvalid(index, 'cpf', labels.invalidCpf)
          isValid = false
        }
      })

      return isValid
    }

    if (section === 1) {
      let isValid = true
      const requiredFields: Array<keyof IInstitutionalState> = [
        'organName', 'organType', 'organDescription', 'cnpj', 'state', 'city', 'annualBudget', 'numberOfServers'
      ]
      requiredFields.forEach(field => {
        const f = institutional[field] as IField
        if (f.value.trim() === '') {
          setInstitutional(prev => ({
            ...prev,
            [field]: { ...(prev[field] as IField), isInvalid: true, message: labels.titleMessage }
          }))
          isValid = false
        }
      })
      if (!institutional.logo) {
        setLogoError(true)
        isValid = false
      }
      const cnpjDigits = institutional.cnpj.value.replace(/\D/g, '')
      if (cnpjDigits.length > 0) {
        if (cnpjDigits.length !== 14) {
          setInstitutional(prev => ({ ...prev, cnpj: { ...prev.cnpj, isInvalid: true, message: 'CNPJ deve ter 14 dígitos' } }))
          isValid = false
        } else if (!isValidCNPJ(cnpjDigits)) {
          setInstitutional(prev => ({ ...prev, cnpj: { ...prev.cnpj, isInvalid: true, message: 'CNPJ inválido' } }))
          isValid = false
        }
      }
      return isValid
    }

    if (section === 2) {
      let isValid = true

      if (leadership.leaderName.value.trim() === '') {
        setLeadership(prev => ({ ...prev, leaderName: { ...prev.leaderName, isInvalid: true, message: labels.titleMessage } }))
        isValid = false
      }

      if (leadership.hasInnovationArea === null) {
        setLeadership(prev => ({ ...prev, hasInnovationAreaInvalid: true }))
        isValid = false
      } else if (leadership.hasInnovationArea === true) {
        if (leadership.innovationDeptName.value.trim() === '') {
          setLeadership(prev => ({ ...prev, innovationDeptName: { ...prev.innovationDeptName, isInvalid: true, message: labels.titleMessage } }))
          isValid = false
        }
        const yearVal = leadership.innovationDeptYear.value.trim()
        if (yearVal === '') {
          setLeadership(prev => ({ ...prev, innovationDeptYear: { ...prev.innovationDeptYear, isInvalid: true, message: labels.titleMessage } }))
          isValid = false
        } else {
          const year = parseInt(yearVal, 10)
          const currentYear = new Date().getFullYear()
          if (year > currentYear) {
            setLeadership(prev => ({ ...prev, innovationDeptYear: { ...prev.innovationDeptYear, isInvalid: true, message: 'O ano não pode ser maior que o ano atual' } }))
            isValid = false
          }
        }
        if (leadership.innovationDeptTeamSize.value.trim() === '') {
          setLeadership(prev => ({ ...prev, innovationDeptTeamSize: { ...prev.innovationDeptTeamSize, isInvalid: true, message: labels.titleMessage } }))
          isValid = false
        }
      }

      if (leadership.hasIctContract === null) {
        setLeadership(prev => ({ ...prev, hasIctContractInvalid: true }))
        isValid = false
      } else if (leadership.hasIctContract === true && leadership.ictContractName.value.trim() === '') {
        setLeadership(prev => ({ ...prev, ictContractName: { ...prev.ictContractName, isInvalid: true, message: labels.titleMessage } }))
        isValid = false
      }

      if (!Object.values(leadership.marcoLegal).some(v => v)) {
        setLeadership(prev => ({ ...prev, marcoLegalInvalid: true }))
        isValid = false
      }

      return isValid
    }

    if (section === 3) {
      let isValid = true

      if (!Object.values(intention.verticais).some(v => v)) {
        setIntention(prev => ({ ...prev, verticaisInvalid: true }))
        isValid = false
      }

      if (!Object.values(intention.objetivos).some(v => v)) {
        setIntention(prev => ({ ...prev, objetivosInvalid: true }))
        isValid = false
      }

      if (intention.hasCtibudget === null) {
        setIntention(prev => ({ ...prev, hasCtibudgetInvalid: true }))
        isValid = false
      } else if (intention.hasCtibudget === true && intention.ctibudgetValue.value.trim() === '') {
        setIntention(prev => ({ ...prev, ctibudgetValue: { ...prev.ctibudgetValue, isInvalid: true, message: labels.titleMessage } }))
        isValid = false
      }

      if (!intention.acceptTerms) {
        setIntention(prev => ({ ...prev, acceptTermsInvalid: true }))
        isValid = false
      }

      return isValid
    }

    return true
  }

  const nextStep = (): void => {
    if (isValidSection(step)) {
      setStep(prev => prev + 1)
      window.scrollTo(0, 0)
      return
    }

    showToast('warning', labels.incompleteStepMessage)
    setShouldScrollToError(true)
  }

  const backStep = (): void => {
    setStep(prev => prev - 1)
    window.scrollTo(0, 0)
  }

  const handleRegister = async (): Promise<void> => {
    for (let s = 0; s <= 3; s++) {
      if (!isValidSection(s)) {
        setStep(s)
        showToast('warning', labels.incompleteFieldsMessage)
        setShouldScrollToError(true)
        return
      }
    }

    try {
      let profileImage: ProfileImagePayload | null = null
      if (institutional.logo) {
        try {
          profileImage = await fileToProfileImagePayload(institutional.logo)
        } catch (readErr) {
          showToast(
            'error',
            readErr instanceof Error && readErr.message.trim() !== ''
              ? readErr.message
              : 'Não foi possível ler o arquivo da logo.'
          )
          return
        }
      }

      const registerPost = createPublicAdministrationPost({
        focalPoints: focalPoints.map(fp => ({
          id: fp.id,
          name: fp.name.value,
          email: fp.email.value,
          phone: fp.phone.value,
          cpf: fp.cpf.value,
          role: fp.role.value,
          linkedin: fp.linkedin.value
        })),
        institutional: {
          organName: institutional.organName.value,
          organType: institutional.organType.value,
          organDescription: institutional.organDescription.value,
          cnpj: institutional.cnpj.value,
          state: institutional.state.value,
          city: institutional.city.value,
          populationSize: institutional.populationSize.value,
          annualBudget: institutional.annualBudget.value,
          numberOfServers: institutional.numberOfServers.value
        },
        leadership: {
          leaderName: leadership.leaderName.value,
          mandateUntil: leadership.mandateUntil.value,
          hasInnovationArea: leadership.hasInnovationArea,
          innovationDeptName: leadership.innovationDeptName.value,
          innovationDeptYear: leadership.innovationDeptYear.value,
          innovationDeptTeamSize: leadership.innovationDeptTeamSize.value,
          hasIctContract: leadership.hasIctContract,
          ictContractName: leadership.ictContractName.value,
          marcoLegal: leadership.marcoLegal
        },
        intention: {
          verticais: intention.verticais,
          objetivos: intention.objetivos,
          hasCtibudget: intention.hasCtibudget,
          ctibudgetValue: intention.ctibudgetValue.value,
          acceptTerms: intention.acceptTerms
        },
        profileImage
      })

      const response = await submitPublicAdministration(registerPost)
      if (response.status < 200 || response.status >= 300) {
        showToast(
          'error',
          parseRegisterApiError(
            { response: { status: response.status, data: response.data } },
            labels.registerApiError
          )
        )
        return
      }
      if (!isSuccessfulPublicAdministrationResponse(response)) {
        showToast('error', labels.registerApiError)
        return
      }
      showToast('success', labels.registerSuccess)
      window.setTimeout(() => {
        navigate(ROUTES.USER_AUTH.LOGIN, { state: { hotsite: true } })
      }, 2000)
    } catch (err) {
      let message = labels.registerApiError
      try {
        message = parseRegisterApiError(err, labels.registerApiError)
      } catch {
        message = labels.registerApiError
      }
      showToast('error', message)
    }
  }

  const getSectionTitle = (): string => {
    switch (step) {
      case 0: return labels.representativesTitle
      case 1: return labels.institutionalTitle
      case 2: return labels.leadershipTitle
      case 3: return labels.intentionTitle
      default: return ''
    }
  }

  const renderFocalPoint = (fp: IFocalPoint, index: number): JSX.Element => (
    <FocalPointCard key={fp.id}>
      {index > 0 && <FocalPointDivider />}

      <Row>
        <Col lg={true} className='d-flex align-items-center justify-content-between'>
          {index > 0 && <FocalPointTitle>{`Ponto focal ${index}`}</FocalPointTitle>}
          {focalPoints.length > 1 && index > 0 && (
            <RemoveFocalPointButton
              type='button'
              onClick={() => removeFocalPoint(index)}
              title='Remover ponto focal'
              aria-label='Remover ponto focal'
            >
              <FontAwesomeIcon icon={['fas', 'times']} size='lg' />
            </RemoveFocalPointButton>
          )}
        </Col>
      </Row>

      <Row className='my-2'>
        <Col lg={true}>
          <Input
            id={`name-${fp.id}`}
            isRequired={true}
            label={labels.focalPointName}
            placeholder='Digite seu nome completo'
            value={fp.name.value}
            isInvalid={fp.name.isInvalid}
            notValidFieldMessage={fp.name.message}
            title={applyTitleOnError(fp.name.message, labels.titleMessage)}
            onChange={(e) => updateFocalPoint(index, 'name', e.target.value)}
            onBlur={() => validateFocalPointField(index, 'name')}
          />
        </Col>
      </Row>

      <Row className='my-2'>
        <Col lg={true}>
          <Input
            id={`email-${fp.id}`}
            isRequired={true}
            label={labels.focalPointEmail}
            placeholder='Digite seu e-mail'
            value={fp.email.value}
            isInvalid={fp.email.isInvalid}
            notValidFieldMessage={fp.email.message}
            title={applyTitleOnError(fp.email.message, labels.titleMessage)}
            onChange={(e) => updateFocalPoint(index, 'email', e.target.value.trim())}
            onBlur={() => {
              const val = fp.email.value.trim()
              if (val === '') {
                setFocalPointFieldInvalid(index, 'email', labels.titleMessage)
              } else if (!EMAIL_REGEX.test(val)) {
                setFocalPointFieldInvalid(index, 'email', labels.invalidFormat)
              } else {
                clearFocalPointFieldError(index, 'email')
              }
            }}
          />
        </Col>
      </Row>

      <Row className='my-2'>
        <Col lg={true}>
          <Input
            id={`phone-${fp.id}`}
            isRequired={true}
            label={labels.focalPointPhone}
            placeholder='Digite seu telefone de contato'
            value={fp.phone.value}
            code='masked'
            mask={applyMaskIfNotEmpty(fp.phone.value, INPUT_MASK_FORMAT.MOBILE)}
            isInvalid={fp.phone.isInvalid}
            notValidFieldMessage={fp.phone.message}
            title={applyTitleOnError(fp.phone.message, labels.titleMessage)}
            onChange={(e) => updateFocalPoint(index, 'phone', e.target.value)}
            onBlur={() => {
              const digits = fp.phone.value.replace(/\D/g, '')
              if (fp.phone.value.trim() === '') {
                setFocalPointFieldInvalid(index, 'phone', labels.titleMessage)
              } else if (digits.length < 10) {
                setFocalPointFieldInvalid(index, 'phone', labels.invalidPhone)
              } else {
                clearFocalPointFieldError(index, 'phone')
              }
            }}
          />
        </Col>
      </Row>

      <Row className='my-2'>
        <Col lg={true}>
          <Input
            id={`cpf-${fp.id}`}
            label={labels.focalPointCpf}
            placeholder='000.000.000-00'
            value={fp.cpf.value}
            code='masked'
            mask={applyMaskIfNotEmpty(fp.cpf.value, INPUT_MASK_FORMAT.CPF)}
            isInvalid={fp.cpf.isInvalid}
            notValidFieldMessage={fp.cpf.message}
            onChange={(e) => {
              const value = e.target.value
              const digits = value.replace(/\D/g, '')
              const invalid = digits.length === 11 && !isValidCPF(digits)
              setFocalPoints(prev =>
                prev.map((fp, i) =>
                  i === index
                    ? {
                      ...fp,
                      cpf: {
                        ...fp.cpf,
                        value,
                        isInvalid: invalid,
                        message: invalid ? labels.invalidCpf : ''
                      }
                    }
                    : fp
                )
              )
            }}
            onBlur={() => {
              const digits = fp.cpf.value.replace(/\D/g, '')
              if (digits.length === 0) {
                clearFocalPointFieldError(index, 'cpf')
              } else if (digits.length !== 11 || !isValidCPF(digits)) {
                setFocalPointFieldInvalid(index, 'cpf', labels.invalidCpf)
              } else {
                clearFocalPointFieldError(index, 'cpf')
              }
            }}
          />
        </Col>
      </Row>

      <Row className='my-2'>
        <Col lg={true}>
          <Input
            id={`role-${fp.id}`}
            isRequired={true}
            label={labels.focalPointRole}
            placeholder='Digite seu cargo'
            value={fp.role.value}
            isInvalid={fp.role.isInvalid}
            notValidFieldMessage={fp.role.message}
            title={applyTitleOnError(fp.role.message, labels.titleMessage)}
            onChange={(e) => updateFocalPoint(index, 'role', e.target.value)}
            onBlur={() => validateFocalPointField(index, 'role')}
          />
        </Col>
      </Row>

      <Row className='my-2'>
        <Col lg={true}>
          <Input
            id={`linkedin-${fp.id}`}
            label={labels.focalPointLinkedin}
            placeholder='Digite seu LinkedIn'
            value={fp.linkedin.value}
            isInvalid={fp.linkedin.isInvalid}
            notValidFieldMessage={fp.linkedin.message}
            onChange={(e) => updateFocalPoint(index, 'linkedin', e.target.value)}
            onBlur={() => clearFocalPointFieldError(index, 'linkedin')}
          />
        </Col>
      </Row>
    </FocalPointCard>
  )

  const renderInstitutionalSection = (): JSX.Element => {
    const inst = institutional
    return (
      <>
        <Row className='my-2'>
          <Col lg={true}>
            <Input
              id='organName'
              isRequired={true}
              label='Nome do órgão'
              placeholder='Digite o nome do órgão'
              value={inst.organName.value}
              isInvalid={inst.organName.isInvalid}
              notValidFieldMessage={inst.organName.message}
              title={applyTitleOnError(inst.organName.message, labels.titleMessage)}
              onChange={(e) => updateInstitutional('organName', e.target.value)}
              onBlur={() => validateInstitutionalField('organName')}
            />
          </Col>
        </Row>

        <Row className='my-2'>
          <Col lg={true}>
            <FieldLabel htmlFor='organType'>
              Tipo de órgão<span className='required'>*</span>
            </FieldLabel>
            <StyledSelect
              id='organType'
              value={inst.organType.value}
              isInvalid={inst.organType.isInvalid}
              onChange={(e) => updateInstitutional('organType', e.target.value)}
              onBlur={() => validateInstitutionalField('organType')}
            >
              {DROPDOWN_ORGAN_TYPE.map((opt, i) => (
                <option key={i} value={opt.value}>{opt.label}</option>
              ))}
            </StyledSelect>
            {inst.organType.isInvalid
              ? <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>{inst.organType.message}</Form.Control.Feedback>
              : <div style={{ height: '20px' }} />
            }
          </Col>
        </Row>

        <Row className='my-2'>
          <Col lg={true}>
            <FieldLabel htmlFor='organDescription'>
              Sobre o órgão público<span className='required'>*</span>
            </FieldLabel>
            <StyledTextarea
              id='organDescription'
              rows={3}
              placeholder='Descreva o órgão público'
              value={inst.organDescription.value}
              isInvalid={inst.organDescription.isInvalid}
              onChange={(e) => updateInstitutional('organDescription', e.target.value)}
              onBlur={() => validateInstitutionalField('organDescription')}
            />
            {inst.organDescription.isInvalid
              ? <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>{inst.organDescription.message}</Form.Control.Feedback>
              : <div style={{ height: '20px' }} />
            }
          </Col>
        </Row>

        <Row className='my-2'>
          <Col lg={true}>
            <Input
              id='cnpj'
              isRequired={true}
              label='CNPJ'
              placeholder='00.000.000/0000-00'
              value={inst.cnpj.value}
              code='masked'
              mask={applyMaskIfNotEmpty(inst.cnpj.value, INPUT_MASK_FORMAT.CNPJ)}
              isInvalid={inst.cnpj.isInvalid}
              notValidFieldMessage={inst.cnpj.message}
              title={applyTitleOnError(inst.cnpj.message, labels.titleMessage)}
              onChange={(e) => {
                const value = e.target.value
                const digits = value.replace(/\D/g, '')
                const invalid = digits.length === 14 && !isValidCNPJ(digits)
                setInstitutional(prev => ({
                  ...prev,
                  cnpj: {
                    ...prev.cnpj,
                    value,
                    isInvalid: invalid,
                    message: invalid ? 'CNPJ inválido' : ''
                  }
                }))
              }}
              onBlur={() => {
                const digits = inst.cnpj.value.replace(/\D/g, '')
                if (digits.length === 0) {
                  setInstitutional(prev => ({ ...prev, cnpj: { ...prev.cnpj, isInvalid: true, message: labels.titleMessage } }))
                } else if (digits.length !== 14) {
                  setInstitutional(prev => ({ ...prev, cnpj: { ...prev.cnpj, isInvalid: true, message: 'CNPJ deve ter 14 dígitos' } }))
                } else if (!isValidCNPJ(digits)) {
                  setInstitutional(prev => ({ ...prev, cnpj: { ...prev.cnpj, isInvalid: true, message: 'CNPJ inválido' } }))
                } else {
                  setInstitutional(prev => ({ ...prev, cnpj: { ...prev.cnpj, isInvalid: false, message: '' } }))
                }
              }}
            />
          </Col>
        </Row>

        <Row className='my-2'>
          <Col lg={true}>
            <FieldLabel htmlFor='state'>
              Estado<span className='required'>*</span>
            </FieldLabel>
            <StyledSelect
              id='state'
              value={inst.state.value}
              isInvalid={inst.state.isInvalid}
              onChange={(e) => updateInstitutionalStateUf(e.target.value)}
              onBlur={() => validateInstitutionalField('state')}
            >
              {DROPDOWN_BRAZIL_STATES.map((opt, i) => (
                <option key={i} value={opt.value}>{opt.label}</option>
              ))}
            </StyledSelect>
            {inst.state.isInvalid
              ? <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>{inst.state.message}</Form.Control.Feedback>
              : <div style={{ height: '20px' }} />
            }
          </Col>
        </Row>

        <Row className='my-2'>
          <Col lg={true}>
            <CitySelector
              id='city'
              label='Cidade'
              isRequired={true}
              stateUf={inst.state.value}
              value={inst.city.value}
              isInvalid={inst.city.isInvalid}
              errorMessage={inst.city.message}
              onChange={(city) => updateInstitutional('city', city)}
              onBlur={() => validateInstitutionalField('city')}
            />
          </Col>
        </Row>

        <Row className='my-2'>
          <Col lg={true}>
            <FieldLabel htmlFor='populationSize' className='d-flex align-items-center gap-2'>
              Porte populacional (se município)
              <OverlayTrigger
                placement='top'
                overlay={
                  <Tooltip id='population-size-tooltip'>
                    Esta classificação é utilizada pelo IBGE e pelo Ministério das Cidades
                  </Tooltip>
                }
              >
                <span style={{ cursor: 'help', color: '#555' }}>
                  <FontAwesomeIcon icon={['fas', 'info-circle']} />
                </span>
              </OverlayTrigger>
            </FieldLabel>
            <StyledSelect
              id='populationSize'
              value={inst.populationSize.value}
              isInvalid={inst.populationSize.isInvalid}
              onChange={(e) => updateInstitutional('populationSize', e.target.value)}
              onBlur={() => validateInstitutionalField('populationSize')}
            >
              {DROPDOWN_POPULATION_SIZE.map((opt, i) => (
                <option key={i} value={opt.value}>{opt.label}</option>
              ))}
            </StyledSelect>
            {inst.populationSize.isInvalid
              ? <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>{inst.populationSize.message}</Form.Control.Feedback>
              : <div style={{ height: '20px' }} />
            }
          </Col>
        </Row>

        <Row className='my-2'>
          <Col lg={true}>
            <div onKeyDown={blockNonNumeric}>
              <Input
                id='annualBudget'
                isRequired={true}
                label='Orçamento anual estimado'
                placeholder='R$ 0'
                value={inst.annualBudget.value ? formatBRL(inst.annualBudget.value) : ''}
                isInvalid={inst.annualBudget.isInvalid}
                notValidFieldMessage={inst.annualBudget.message}
                title={applyTitleOnError(inst.annualBudget.message, labels.titleMessage)}
                onChange={(e) => updateInstitutional('annualBudget', onlyDigits(e.target.value))}
                onBlur={() => validateInstitutionalField('annualBudget')}
              />
            </div>
          </Col>
        </Row>

        <Row className='my-2'>
          <Col lg={true}>
            <div onKeyDown={blockNonNumeric}>
              <Input
                id='numberOfServers'
                isRequired={true}
                label='Número de servidores'
                placeholder='Digite o número de servidores'
                value={inst.numberOfServers.value}
                isInvalid={inst.numberOfServers.isInvalid}
                notValidFieldMessage={inst.numberOfServers.message}
                title={applyTitleOnError(inst.numberOfServers.message, labels.titleMessage)}
                onChange={(e) => updateInstitutional('numberOfServers', e.target.value.replace(/\D/g, ''))}
                onBlur={() => validateInstitutionalField('numberOfServers')}
              />
            </div>
          </Col>
        </Row>

        <Row className='my-2'>
          <Col lg={true}>
            <FieldLabel>Logo do Órgão Público<span className='required'>*</span></FieldLabel>
            <div className='text-muted small mb-2'>
              Dimensão recomendada: 500 × 600
            </div>
            <div
              className='d-flex align-items-center gap-3'
              data-invalid={logoError && !inst.logo ? 'true' : undefined}
            >
              {!inst.logo && (
                <AddLogoButton>
                  <FontAwesomeIcon icon={['fas', 'cloud-upload-alt']} size='lg' />
                  Adicionar logo
                  <input
                    ref={logoInputRef}
                    required={true}
                    type='file'
                    accept='image/jpeg,image/png,image/jpg,image/gif'
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null
                      if (!file) return
                      if (file.size > 5242880) {
                        showToast('warning', 'Cada arquivo deve ter um tamanho máximo de 5 MB')
                        e.target.value = ''
                        return
                      }
                      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif']
                      if (!allowedTypes.includes(file.type)) {
                        showToast('warning', `Arquivo ${file.name} não é permitido.`)
                        e.target.value = ''
                        return
                      }
                      const preview = URL.createObjectURL(file)
                      setInstitutional(prev => ({ ...prev, logo: file, logoPreview: preview }))
                      setLogoError(false)
                    }}
                  />
                </AddLogoButton>
              )}
              {logoError && !inst.logo && (
                <LogoPreviewError>
                  {labels.logoRequiredMessage}
                </LogoPreviewError>
              )}

              {inst.logo && inst.logoPreview && (
                <div className='position-relative'>
                  <LogoPreviewImage src={inst.logoPreview} alt='Logo preview' />
                  <RemoveLogoButton
                    type='button'
                    onClick={() => {
                      setInstitutional(prev => ({ ...prev, logo: null, logoPreview: null }))
                      if (logoInputRef.current) logoInputRef.current.value = ''
                    }}
                    title='Remover logo'
                  >
                    ×
                  </RemoveLogoButton>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </>
    )
  }

  const renderLeadershipSection = (): JSX.Element => {
    const ld = leadership

    const innovationRadioItems: RadioGroupItemProps[] = [
      {
        code: 'radio',
        name: 'hasInnovationArea',
        label: 'Sim',
        value: 'true',
        checked: ld.hasInnovationArea === true,
        onClick: () => setLeadership(prev => ({ ...prev, hasInnovationArea: true, hasInnovationAreaInvalid: false }))
      },
      {
        code: 'radio',
        name: 'hasInnovationArea',
        label: 'Não',
        value: 'false',
        checked: ld.hasInnovationArea === false,
        onClick: () => setLeadership(prev => ({
          ...prev,
          hasInnovationArea: false,
          hasInnovationAreaInvalid: false,
          innovationDeptName: { ...prev.innovationDeptName, value: '', isInvalid: false, message: '' },
          innovationDeptYear: { ...prev.innovationDeptYear, value: '', isInvalid: false, message: '' },
          innovationDeptTeamSize: { ...prev.innovationDeptTeamSize, value: '', isInvalid: false, message: '' }
        }))
      }
    ]

    const ictRadioItems: RadioGroupItemProps[] = [
      {
        code: 'radio',
        name: 'hasIctContract',
        label: 'Sim',
        value: 'true',
        checked: ld.hasIctContract === true,
        onClick: () => setLeadership(prev => ({ ...prev, hasIctContract: true, hasIctContractInvalid: false }))
      },
      {
        code: 'radio',
        name: 'hasIctContract',
        label: 'Não',
        value: 'false',
        checked: ld.hasIctContract === false,
        onClick: () => setLeadership(prev => ({
          ...prev,
          hasIctContract: false,
          hasIctContractInvalid: false,
          ictContractName: { ...prev.ictContractName, value: '', isInvalid: false, message: '' }
        }))
      }
    ]

    return (
      <>
        <Row className='my-2'>
          <Col lg={true}>
            <Input
              id='leaderName'
              isRequired={true}
              label='Nome do dirigente máximo (Prefeito, Secretário, Presidente, Diretor):'
              placeholder='Digite o nome do dirigente'
              value={ld.leaderName.value}
              isInvalid={ld.leaderName.isInvalid}
              notValidFieldMessage={ld.leaderName.message}
              title={applyTitleOnError(ld.leaderName.message, labels.titleMessage)}
              onChange={(e) => setLeadership(prev => ({ ...prev, leaderName: { ...prev.leaderName, value: e.target.value, isInvalid: false, message: '' } }))}
              onBlur={() => {
                if (ld.leaderName.value.trim() === '') {
                  setLeadership(prev => ({ ...prev, leaderName: { ...prev.leaderName, isInvalid: true, message: labels.titleMessage } }))
                }
              }}
            />
          </Col>
        </Row>

        <Row className='my-2'>
          <Col lg={true}>
            <Input
              id='mandateUntil'
              label='Mandato até'
              placeholder='00/00/0000'
              value={ld.mandateUntil.value}
              code='masked'
              mask={INPUT_MASK_FORMAT.DATE}
              isInvalid={ld.mandateUntil.isInvalid}
              notValidFieldMessage={ld.mandateUntil.message}
              title={applyTitleOnError(ld.mandateUntil.message, labels.titleMessage)}
              onChange={(e) => setLeadership(prev => ({
                ...prev,
                mandateUntil: { ...prev.mandateUntil, value: e.target.value, isInvalid: false, message: '' }
              }))}
            />
          </Col>
        </Row>

        <Row className='my-2'>
          <Col lg={true}>
            <div data-invalid={ld.hasInnovationAreaInvalid ? 'true' : undefined}>
              <Input
                id='hasInnovationArea'
                code='radio-group'
                name='hasInnovationArea'
                label='Possui área formal de inovação?'
                radioItems={innovationRadioItems}
                value={ld.hasInnovationArea === true ? 'true' : ld.hasInnovationArea === false ? 'false' : ''}
                isRequired={true}
                isInvalid={ld.hasInnovationAreaInvalid}
                notValidFieldMessage={ld.hasInnovationAreaInvalid ? labels.titleMessage : ''}
                onChange={() => {}}
              />
            </div>
          </Col>
        </Row>

        {ld.hasInnovationArea === true && (
          <>
            <Row className='my-2'>
              <Col lg={true}>
                <Input
                  id='innovationDeptName'
                  isRequired={true}
                  label='Nome da Secretaria/Departamento'
                  placeholder='Digite o nome da secretaria'
                  value={ld.innovationDeptName.value}
                  isInvalid={ld.innovationDeptName.isInvalid}
                  notValidFieldMessage={ld.innovationDeptName.message}
                  title={applyTitleOnError(ld.innovationDeptName.message, labels.titleMessage)}
                  onChange={(e) => setLeadership(prev => ({ ...prev, innovationDeptName: { ...prev.innovationDeptName, value: e.target.value, isInvalid: false, message: '' } }))}
                  onBlur={() => {
                    if (ld.innovationDeptName.value.trim() === '') {
                      setLeadership(prev => ({ ...prev, innovationDeptName: { ...prev.innovationDeptName, isInvalid: true, message: labels.titleMessage } }))
                    }
                  }}
                />
              </Col>
            </Row>
            <Row className='my-2'>
              <Col lg={true}>
                <div onKeyDown={blockNonNumericMax(4)}>
                  <Input
                    id='innovationDeptYear'
                    isRequired={true}
                    label='Ano de criação'
                    placeholder='AAAA'
                    value={ld.innovationDeptYear.value}
                    isInvalid={ld.innovationDeptYear.isInvalid}
                    notValidFieldMessage={ld.innovationDeptYear.message}
                    title={applyTitleOnError(ld.innovationDeptYear.message, labels.titleMessage)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 4)
                      const invalid = value.length === 4 && parseInt(value, 10) > new Date().getFullYear()
                      setLeadership(prev => ({
                        ...prev,
                        innovationDeptYear: {
                          ...prev.innovationDeptYear,
                          value,
                          isInvalid: invalid,
                          message: invalid ? 'O ano não pode ser maior que o ano atual' : ''
                        }
                      }))
                    }}
                    onBlur={() => {
                      const yearVal = ld.innovationDeptYear.value.trim()
                      if (yearVal === '') {
                        setLeadership(prev => ({ ...prev, innovationDeptYear: { ...prev.innovationDeptYear, isInvalid: true, message: labels.titleMessage } }))
                      } else {
                        const year = parseInt(yearVal, 10)
                        if (year > new Date().getFullYear()) {
                          setLeadership(prev => ({ ...prev, innovationDeptYear: { ...prev.innovationDeptYear, isInvalid: true, message: 'O ano não pode ser maior que o ano atual' } }))
                        } else {
                          setLeadership(prev => ({ ...prev, innovationDeptYear: { ...prev.innovationDeptYear, isInvalid: false, message: '' } }))
                        }
                      }
                    }}
                  />
                </div>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col lg={true}>
                <div onKeyDown={blockNonNumeric}>
                  <Input
                    id='innovationDeptTeamSize'
                    isRequired={true}
                    label='Quantidade de pessoas na equipe'
                    placeholder='Digite a quantidade de pessoas'
                    value={ld.innovationDeptTeamSize.value}
                    isInvalid={ld.innovationDeptTeamSize.isInvalid}
                    notValidFieldMessage={ld.innovationDeptTeamSize.message}
                    title={applyTitleOnError(ld.innovationDeptTeamSize.message, labels.titleMessage)}
                    onChange={(e) => setLeadership(prev => ({ ...prev, innovationDeptTeamSize: { ...prev.innovationDeptTeamSize, value: e.target.value.replace(/\D/g, ''), isInvalid: false, message: '' } }))}
                    onBlur={() => {
                      if (ld.innovationDeptTeamSize.value.trim() === '') {
                        setLeadership(prev => ({ ...prev, innovationDeptTeamSize: { ...prev.innovationDeptTeamSize, isInvalid: true, message: labels.titleMessage } }))
                      }
                    }}
                  />
                </div>
              </Col>
            </Row>
          </>
        )}

        <Row className='my-2'>
          <Col lg={true}>
            <div data-invalid={ld.hasIctContractInvalid ? 'true' : undefined}>
              <Input
                id='hasIctContract'
                code='radio-group'
                name='hasIctContract'
                label='Tem contrato de gestão com alguma ICT (Instituição de Ciência e Tecnologia), Parque Tecnológico, Hub, etc?'
                radioItems={ictRadioItems}
                value={ld.hasIctContract === true ? 'true' : ld.hasIctContract === false ? 'false' : ''}
                isRequired={true}
                isInvalid={ld.hasIctContractInvalid}
                notValidFieldMessage={ld.hasIctContractInvalid ? labels.titleMessage : ''}
                onChange={() => {}}
              />
            </div>
          </Col>
        </Row>

        {ld.hasIctContract === true && (
          <Row className='my-2'>
            <Col lg={true}>
              <Input
                id='ictContractName'
                isRequired={true}
                label='Qual?'
                placeholder='Digite o nome da ICT'
                value={ld.ictContractName.value}
                isInvalid={ld.ictContractName.isInvalid}
                notValidFieldMessage={ld.ictContractName.message}
                title={applyTitleOnError(ld.ictContractName.message, labels.titleMessage)}
                onChange={(e) => setLeadership(prev => ({ ...prev, ictContractName: { ...prev.ictContractName, value: e.target.value, isInvalid: false, message: '' } }))}
                onBlur={() => {
                  if (ld.ictContractName.value.trim() === '') {
                    setLeadership(prev => ({ ...prev, ictContractName: { ...prev.ictContractName, isInvalid: true, message: labels.titleMessage } }))
                  }
                }}
              />
            </Col>
          </Row>
        )}

        <Row className='my-2'>
          <Col lg={true}>
            <div data-invalid={ld.marcoLegalInvalid ? 'true' : undefined}>
              <FieldLabel>
                Já realizou procedimentos do marco legal para contratação inovadora?
                <span className='required'>*</span>
              </FieldLabel>
              {ld.marcoLegalInvalid && (
                <div style={{ color: '#dc3545', fontSize: '0.8rem', marginBottom: '4px' }}>
                  {labels.titleMessage}
                </div>
              )}
              <CheckboxGroup>
                {MARCO_LEGAL_OPTIONS.map(opt => (
                  <Form.Check
                    key={opt.id}
                    type='checkbox'
                    id={`marcoLegal-${opt.id}`}
                    label={opt.label}
                    checked={ld.marcoLegal[opt.id]}
                    onChange={(e) => {
                      setLeadership(prev => ({
                        ...prev,
                        marcoLegal: { ...prev.marcoLegal, [opt.id]: e.target.checked },
                        marcoLegalInvalid: false
                      }))
                    }}
                  />
                ))}
              </CheckboxGroup>
            </div>
          </Col>
        </Row>
      </>
    )
  }

  const renderIntentionSection = (): JSX.Element => {
    const int = intention

    const ctiRadioItems: RadioGroupItemProps[] = [
      {
        code: 'radio',
        name: 'hasCtibudget',
        label: 'Sim',
        value: 'true',
        checked: int.hasCtibudget === true,
        onClick: () => setIntention(prev => ({ ...prev, hasCtibudget: true, hasCtibudgetInvalid: false }))
      },
      {
        code: 'radio',
        name: 'hasCtibudget',
        label: 'Não',
        value: 'false',
        checked: int.hasCtibudget === false,
        onClick: () => setIntention(prev => ({
          ...prev,
          hasCtibudget: false,
          hasCtibudgetInvalid: false,
          ctibudgetValue: { ...prev.ctibudgetValue, value: '', isInvalid: false, message: '' }
        }))
      }
    ]

    return (
      <>
        <Row className='my-2'>
          <Col lg={true}>
            <div data-invalid={int.verticaisInvalid ? 'true' : undefined}>
              <FieldLabel>
                Verticais de Impacto Prioritários<span className='required'>*</span>
              </FieldLabel>
              {int.verticaisInvalid && (
                <div style={{ color: '#dc3545', fontSize: '0.8rem', marginBottom: '4px' }}>
                  {labels.titleMessage}
                </div>
              )}
              <CheckboxGroup>
                {VERTICAIS_OPTIONS.map(opt => (
                  <Form.Check
                    key={opt.id}
                    type='checkbox'
                    id={`verticais-${opt.id}`}
                    label={opt.label}
                    checked={int.verticais[opt.id]}
                    onChange={(e) => {
                      setIntention(prev => ({
                        ...prev,
                        verticais: { ...prev.verticais, [opt.id]: e.target.checked },
                        verticaisInvalid: false
                      }))
                    }}
                  />
                ))}
              </CheckboxGroup>
            </div>
          </Col>
        </Row>

        <Row className='my-2'>
          <Col lg={true}>
            <div data-invalid={int.objetivosInvalid ? 'true' : undefined}>
              <FieldLabel>
                Objetivo ao entrar no programa de residentes do Espaço TEIA:<span className='required'>*</span>
              </FieldLabel>
              {int.objetivosInvalid && (
                <div style={{ color: '#dc3545', fontSize: '0.8rem', marginBottom: '4px' }}>
                  {labels.titleMessage}
                </div>
              )}
              <CheckboxGroup>
                {OBJETIVOS_OPTIONS.map(opt => (
                  <Form.Check
                    key={opt.id}
                    type='checkbox'
                    id={`objetivos-${opt.id}`}
                    label={opt.label}
                    checked={int.objetivos[opt.id]}
                    onChange={(e) => {
                      setIntention(prev => ({
                        ...prev,
                        objetivos: { ...prev.objetivos, [opt.id]: e.target.checked },
                        objetivosInvalid: false
                      }))
                    }}
                  />
                ))}
              </CheckboxGroup>
            </div>
          </Col>
        </Row>

        <Row className='my-2'>
          <Col lg={true}>
            <div data-invalid={int.hasCtibudgetInvalid ? 'true' : undefined}>
              <Input
                id='hasCtibudget'
                code='radio-group'
                name='hasCtibudget'
                label='Existe orçamento para CT&I (Ciência, Tecnologia e Inovação)?'
                radioItems={ctiRadioItems}
                value={int.hasCtibudget === true ? 'true' : int.hasCtibudget === false ? 'false' : ''}
                isRequired={true}
                isInvalid={int.hasCtibudgetInvalid}
                notValidFieldMessage={int.hasCtibudgetInvalid ? labels.titleMessage : ''}
                onChange={() => {}}
              />
            </div>
          </Col>
        </Row>

        {int.hasCtibudget === true && (
          <Row className='my-2'>
            <Col lg={true}>
              <div onKeyDown={blockNonNumeric}>
                <Input
                  id='ctibudgetValue'
                  isRequired={true}
                  label='Valor estimado anual disponível para CT&I (Ciência, Tecnologia e Inovação):'
                  placeholder='R$ 0'
                  value={int.ctibudgetValue.value ? formatBRL(int.ctibudgetValue.value) : ''}
                  isInvalid={int.ctibudgetValue.isInvalid}
                  notValidFieldMessage={int.ctibudgetValue.message}
                  title={applyTitleOnError(int.ctibudgetValue.message, labels.titleMessage)}
                  onChange={(e) => setIntention(prev => ({
                    ...prev,
                    ctibudgetValue: { ...prev.ctibudgetValue, value: onlyDigits(e.target.value), isInvalid: false, message: '' }
                  }))}
                  onBlur={() => {
                    if (int.ctibudgetValue.value.trim() === '') {
                      setIntention(prev => ({
                        ...prev,
                        ctibudgetValue: { ...prev.ctibudgetValue, isInvalid: true, message: labels.titleMessage }
                      }))
                    }
                  }}
                />
              </div>
            </Col>
          </Row>
        )}

        <Row className='my-2'>
          <Col lg={true}>
            <div data-invalid={int.acceptTermsInvalid ? 'true' : undefined}>
              <CheckboxGroup style={{ marginTop: '8px' }}>
                <UserTerms
                  userType='company'
                  checked={int.acceptTerms}
                  onCheck={(e) => {
                    setIntention(prev => ({
                      ...prev,
                      acceptTerms: e.target.checked,
                      acceptTermsInvalid: e.target.checked ? false : prev.acceptTermsInvalid
                    }))
                  }}
                />
              </CheckboxGroup>
              {int.acceptTermsInvalid && (
                <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '4px' }}>
                  {labels.titleMessage}
                </div>
              )}
            </div>
          </Col>
        </Row>
      </>
    )
  }

  const renderSection = (): JSX.Element => {
    switch (step) {
      case 0:
        return (
          <>
            {focalPoints.map((fp, index) => renderFocalPoint(fp, index))}
            <Row>
              <Col lg={true}>
                <AddFocalPointButton type='button' onClick={addFocalPoint}>
                  <FontAwesomeIcon icon={['fas', 'plus']} size='sm' />
                  {labels.addFocalPoint}
                </AddFocalPointButton>
              </Col>
            </Row>
          </>
        )
      case 1: return renderInstitutionalSection()
      case 2: return renderLeadershipSection()
      case 3: return renderIntentionSection()
      default: return <></>
    }
  }

  const isLastStep = step === TOTAL_STEPS - 1

  return (
    <form
      style={{ display: 'contents' }}
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
      }}
    >
      <FormWrapper>
        <FormTitle>{labels.formTitle}</FormTitle>
        <HotsiteFormMain formTitle=''>
          <StepTransition key={step}>
            <SectionTitle hasIcon={step > 0}>
              {step > 0 && (
                <a onClick={backStep} title={labels.back} className='cursor-pointer'>
                  <FontAwesomeIcon size='1x' icon={['fas', 'arrow-alt-circle-left']} />
                </a>
              )}
              <span>{getSectionTitle()}</span>
            </SectionTitle>
            {renderSection()}
          </StepTransition>

          <StepButtonsContainer $isLastStep={isLastStep}>
            {isLastStep
              ? (
                <CadastrarButton type='button' disabled={isSubmitting} onClick={() => { void handleRegister() }}>
                  {isSubmitting ? labels.sending : labels.register}
                </CadastrarButton>
              )
              : <ProximoButton type='button' onClick={nextStep}>{labels.next}</ProximoButton>
            }
          </StepButtonsContainer>

          <hr className='mt-4' />
          <LoginButton />
        </HotsiteFormMain>
      </FormWrapper>
    </form>
  )
}
